import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { map } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { Order, OrderStatus, OrderTypeEnum, OrderValitation } from 'src/app/components/company/models/new/order';
import { OrderMapper } from 'src/app/components/company/services/order-mapper';
import { OrderQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { ChangeOrderStatusComponent } from 'src/app/shared/components/change-order-status/change-order-status.component';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { PhotographerFilterService } from 'src/app/services/filters/photographer-filter.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { Selectable } from 'src/app/shared/types/selectable.type';

@Component({
  selector: 'bt-company-order-detail-info',
  standalone: false,
  templateUrl: './company-order-detail-info.component.html',
  styleUrl: './company-order-detail-info.component.scss'
})
export class CompanyOrderDetailInfoComponent {
  order: Order | undefined = undefined;
  orderValidation: OrderValitation = new OrderValitation();
  isNew: boolean = false;
  orderStatus = OrderStatus;
  loading: boolean = false;
  todayDate: Date = new Date();

  orderTypes: Selectable<{ id: OrderTypeEnum; name: string }>[] = [
    { id: OrderTypeEnum.StillLife, name: 'Still-Life', selected: true },
    { id: OrderTypeEnum.Dressed, name: 'On Model', selected: false },
    { id: OrderTypeEnum.Complete, name: 'Complete', selected: false }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appService: AppService,
    private utilitiesService: UtilitiesService,
    private graphQLService: GraphQLService,
    private dialog: MatDialog,
    private orderMapper: OrderMapper,
    private photographerService: PhotographerFilterService
  ) {}

  ngOnInit(): void {
    this.route.parent?.params.subscribe((params) => {
      const orderId = params['orderId'];

      if (orderId != 'new') {
        this.getOrderInfo(Number(orderId));
        this.isNew = false;
      } else {
        this.order = new Order({
          creationDate: new Date(),
          orderType: this.orderTypes.find((type) => type.selected)?.id
        });
        this.isNew = true;
      }
    });
  }

  getOrderInfo(_orderId: number): void {
    this.loading = true;
    this.graphQLService
      .getData(OrderQueries.GET_ORDERS, { where: { orderId: { eq: _orderId } }, first: 1 })
      .pipe(map((res: any) => this.orderMapper.toOrder(res.data.orders.nodes[0])))
      .subscribe({
        next: (data: Order) => {
          this.order = data;
          console.log(this.order, this.order.orderType);

          this.orderTypes.forEach((type) => (type.selected = type.id == this.order?.orderType));

          this.loading = false;
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error during fetching order info', SnackbarType.Error);
          console.error(err);
          this.loading = false;
        }
      });
  }

  toggleSynchronization(): void {
    if (this.order) {
      this.order.isFake = !this.order.isFake;
    }
  }

  validate(): boolean {
    this.orderValidation.dueDate = this.order?.dueDate ? true : false;
    this.orderValidation.photographer = this.order?.photographer ? true : false;

    return _.every(Object.values(this.orderValidation));
  }

  saveChanges() {
    if (!this.validate()) {
      this.utilitiesService.showSnackbar('Please fill in all required fields before saving changes.');
      return;
    }

    let companyOrderInfoInput: any;

    if (this.isNew) {
      companyOrderInfoInput = {
        statusId: OrderStatus.Draft,
        dueDate: this.order?.dueDate || undefined,
        notes: this.order?.notes || undefined,
        photographerId: this.order?.photographer?.id,
        orderTypeId: this.order?.orderType,
        isFake: this.order?.isFake
      };
    } else {
      companyOrderInfoInput = {
        orderId: this.order?.id,
        statusId: this.order?.status?.id,
        //creationDate: this.order?.creationDate, //da sistemare
        completionDate: this.order?.completionDate || undefined,
        dueDate: this.order?.dueDate || undefined,
        notes: this.order?.notes || undefined,
        photographerId: this.order?.photographer?.id,
        orderTypeId: this.order?.orderType
      };
    }

    const payload = {
      upsertOrderInfoInput: companyOrderInfoInput
    };

    this.loading = true;

    this.graphQLService.postData(MutationQuery.UPSERT_ORDER_INFO, payload).subscribe({
      next: (value: any) => {
        this.loading = false;
        if (this.isNew) {
          this.utilitiesService.showSnackbar('Order created successfully', SnackbarType.Success);
          this.router.navigate(['/company/order-view/' + value.data.upsertOrderInfo.message + '/order-detail-shots']);
        } else this.utilitiesService.showSnackbar('Order updated successfully', SnackbarType.Success);
      },
      error: (err) => {
        this.utilitiesService.showSnackbar(
          err.networkError.error.errors[0].message || 'Error during saving order',
          SnackbarType.Error
        );

        this.loading = false;
      }
    });
  }

  downloadShippingLabel(): void {
    window.open(this.order?.waybill?.waybillUrl, '_blank');
  }

  selectOrderType(orderType: Selectable<{ id: OrderTypeEnum; name: string }>) {
    this.orderTypes.forEach((type) => (type.selected = false));
    orderType.selected = true;
    if (this.order) this.order.orderType = orderType.id;
  }

  openStatusModal(): void {
    const dialog = this.dialog.open(ChangeOrderStatusComponent, {
      data: { currentStatus: this.order?.status }
    });
    dialog.afterClosed().subscribe({
      next: (res) => {
        if (res) {
          this.order!.status = _.cloneDeep(res);
        }
      }
    });
  }

  openEditPhotographerModal(): void {
    this.dialog
      .open(FilterListComponent<any>, {
        data: {
          title: 'Photographer',
          selected: this.order?.photographer
            ? [{ ...this.order.photographer, displayId: this.order.photographer.id }]
            : [],
          filterService: this.photographerService,
          singleSelection: true
        }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            let photographer = res[0];

            this.order!.photographer = photographer;
          }
        }
      });
  }

  forbiddenEdit() {
    if (this.order?.status == undefined) return false; //undefined è un nuovo ordine, ancora senza stato
    return (
      this.order?.status?.id == OrderStatus.Submitted ||
      this.order?.status?.id == OrderStatus.ShootCompleted ||
      this.order?.status?.id == OrderStatus.Rejected ||
      this.order?.status?.id == OrderStatus.InProgress ||
      this.order?.status?.id == OrderStatus.Approved
    );
  }
}
